// Considered: `CheckInFormContent` (the existing dialog) — rejected as a direct reuse. The
// wireframe's inline control is a compact 3-tap pill group with no comment field, submitted
// without leaving the panel; the dialog's dropdown + textarea form is a different interaction for
// a different surface. Both now share `SegmentedControlAtom` for status selection (Rule 3).
// Reuses `useStatusCheckIn` — the hook resolves the aspect's own current pending period from its
// factorId, so this molecule never has to know a periodId itself.
//
// Source: design/metaspace-tools/metaspace-trajectory-v2.jsx:92-113 (`MSCheckIn`). Three render
// paths (idle / open / done) — all hooks are called unconditionally above the branch (S6440).

import React, { useMemo, useState } from 'react';
import { useTranslation } from 'localization';
import type { StatusValue } from 'types/spherePoints';
import GlyphAtom from '../../atoms/Glyph/GlyphAtom';
import ButtonAtom from '../../atoms/Button/ButtonAtom';
import SegmentedControlAtom from '../../atoms/SegmentedControl/SegmentedControlAtom';
import { useStatusCheckIn } from '../../../hooks/useStatusCheckIn';
import './CheckInInlineMolecule.scss';

const DEFAULT_STATUSES: StatusValue[] = ['on_track', 'at_risk', 'needs_attention', 'critical', 'blocked'];

export interface CheckInInlineMoleculeProps {
  sphereId: string;
  factorId: string;
  statuses?: StatusValue[];
  onSubmitted?: (statusValue: StatusValue) => void;
  className?: string;
}

const CheckInInlineMolecule: React.FC<CheckInInlineMoleculeProps> = ({
  sphereId,
  factorId,
  statuses = DEFAULT_STATUSES,
  onSubmitted,
  className,
}) => {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [pick, setPick] = useState<StatusValue | null>(null);
  const [done, setDone] = useState<StatusValue | null>(null);

  const factors = useMemo(
    () => [{ id: factorId, factorType: 'manual' as const, weight: 1, status: 'unknown' as StatusValue }],
    [factorId]
  );

  const { submitCheckIn, isSubmitting } = useStatusCheckIn({ sphereId, factors });

  const handleSubmit = async () => {
    if (!pick) return;
    await submitCheckIn(pick);
    setDone(pick);
    onSubmitted?.(pick);
  };

  const classes = ['check-in-inline-molecule', className].filter(Boolean).join(' ');

  if (done) {
    return (
      <span className={`${classes} check-in-inline-molecule--done`}>
        <GlyphAtom glyph="lucide:check-circle" />
        {t('trajectory.checkIn.checkedIn', { status: t(`statusVocabulary.${done}`), defaultValue: `checked in · ${done}` })}
      </span>
    );
  }

  if (!open) {
    return (
      <span className={classes}>
        <ButtonAtom
          label={t('trajectory.checkIn.checkIn')}
          icon="pi pi-check"
          size="small"
          onClick={(e) => {
            e?.stopPropagation?.();
            setOpen(true);
          }}
        />
      </span>
    );
  }

  return (
    <span className={classes}>
      <SegmentedControlAtom<StatusValue>
        options={statuses.map((value) => ({ value, label: t(`statusVocabulary.${value}`) }))}
        value={pick ?? ('' as StatusValue)}
        onChange={(value) => setPick(value as StatusValue)}
        mode="single"
        variant="pills"
        size="small"
        ariaLabel={t('trajectory.checkIn.checkIn')}
      />
      <ButtonAtom
        label={t('trajectory.checkIn.submit')}
        icon="pi pi-arrow-down-left"
        size="small"
        disabled={!pick || isSubmitting}
        onClick={handleSubmit}
      />
    </span>
  );
};

CheckInInlineMolecule.displayName = 'CheckInInlineMolecule';

export default CheckInInlineMolecule;
